import { useMemo } from "react";
import useAdminQuery from "../../../lib/queries/useAdminQuery";
import { Topbar } from "../../../components/AppShell";
import { LoadingPanel } from "../../../components/LoadingPanel";
import { KpiCard } from "../../../lib/funnel/charts";
import { FilterableTable } from "../../../lib/funnel/FilterableTable";
import type { ColumnSpec } from "../../../lib/funnel/FilterableTable";
import { fmtN } from "../../../lib/funnel/helpers";
import { DOC_GEN_USERS_SQL, mapDocGenUserRows } from "./sql";
import type { DocGenUserRow } from "./sql";

export function DocumentGeneration() {
  const q = useAdminQuery(DOC_GEN_USERS_SQL);

  const rows = useMemo<DocGenUserRow[]>(() => {
    if (!q.data) return [];
    return mapDocGenUserRows(q.data.columns, q.data.rows as unknown[][]);
  }, [q.data]);

  const kpis = useMemo(() => {
    const totalDocs = rows.reduce((s, r) => s + r.doc_count, 0);
    const userCount = rows.length;
    const companyCount = new Set(rows.map((r) => r.company_external_id)).size;
    const avgPerUser = userCount > 0 ? totalDocs / userCount : 0;
    return { totalDocs, userCount, companyCount, avgPerUser };
  }, [rows]);

  const columns: ColumnSpec<DocGenUserRow>[] = useMemo(() => [
    {
      key: "user_name",
      label: "User",
      render: (r) => (
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
          <span style={{ fontWeight: 600 }}>{r.user_name}</span>
          <span style={{ color: "var(--fg-3)", fontSize: 11 }}>{r.user_email}</span>
        </div>
      ),
    },
    { key: "company_name", label: "Company", filterable: true },
    { key: "country_code", label: "Country", filterable: true, render: (r) => r.country_code ?? "—" },
    {
      key: "doc_count",
      label: "Documents",
      align: "right",
      render: (r) => <span className="mono" style={{ fontWeight: 700 }}>{fmtN(r.doc_count)}</span>,
    },
    {
      key: "days_since_company_first_doc",
      label: "Days since company's first doc",
      align: "right",
      render: (r) => (
        <span className="mono">
          {r.days_since_company_first_doc == null ? "—" : fmtN(r.days_since_company_first_doc)}
        </span>
      ),
    },
    {
      key: "days_since_user_first_doc",
      label: "Days since user's first doc",
      align: "right",
      render: (r) => (
        <span className="mono">
          {r.days_since_user_first_doc == null ? "—" : fmtN(r.days_since_user_first_doc)}
        </span>
      ),
    },
  ], []);

  return (
    <>
      <Topbar crumbs={["Dashboards", "Misc", "Document Generation"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Internal · public.document</div>
            <h1>Document Generation</h1>
            <p className="lede">
              One row per user who has ever generated a document. Counts are all-time,
              joined to their company and country. Demo and deleted companies excluded.
            </p>
          </div>
        </div>

        {q.isLoading && rows.length === 0 ? (
          <LoadingPanel />
        ) : q.isError ? (
          <pre style={{ color: "var(--fg-danger)", whiteSpace: "pre-wrap", fontSize: 12 }}>
            {(q.error as Error | undefined)?.message ?? "Failed to load data."}
          </pre>
        ) : rows.length === 0 ? (
          <div className="empty">No documents found.</div>
        ) : (
          <>
            <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              <KpiCard label="Total documents"   value={fmtN(kpis.totalDocs)} />
              <KpiCard label="Active users"      value={fmtN(kpis.userCount)} sub="Users with at least one document" />
              <KpiCard label="Companies"         value={fmtN(kpis.companyCount)} sub="Distinct companies represented" />
              <KpiCard label="Avg docs / user"   value={kpis.avgPerUser.toFixed(1)} />
            </div>

            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-body">
                <FilterableTable
                  rows={rows}
                  columns={columns}
                  rowKey={(r) => `${r.user_external_id}-${r.company_external_id}`}
                  initialSortKey="doc_count"
                  initialSortDir="desc"
                  searchAccessor={(r) => `${r.user_name} ${r.user_email} ${r.company_name}`}
                  searchPlaceholder="Search by user, email, or company"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
